import * as React from "react";
import { Input } from "@/components/ui/input";

/**
 * NumericInput — a defensive wrapper around <input> for whole-number entry
 * (consumer counts, revenue dollars, transaction counts, exact revenue).
 *
 * Why we don't use `<input type="number">` for these fields anymore:
 *
 *   1. `type="number"` inputs SILENTLY MUTATE their value on mouse wheel
 *      scroll while focused — the browser treats the wheel as an increment
 *      step. A merchant who types "50000" then scrolls the page with the
 *      mouse still over the input sees the value change to 49999, 49998,
 *      etc., with NO change event that user code can prevent cleanly.
 *      No network request fires — the mutation is purely client-side.
 *      This produced the "typed 50000, wrong number scored" bug that
 *      persisted for ~1 month (reported 2026-08-18).
 *
 *   2. Up/Down arrow keys also step the value on `type="number"` inputs,
 *      which is easy to trigger by accident while scrolling.
 *
 *   3. `type="number"` also silently drops leading zeros, mangles very
 *      large numbers into exponential notation, and rejects some
 *      non-Latin numeric keyboards.
 *
 * The mainstream fintech-industry fix (Stripe, Plaid, Wise, etc.) is:
 *   • type="text" — no spinner, no wheel step, no arrow step.
 *   • inputMode="numeric" — mobile numeric keypad preserved.
 *   • pattern="[0-9]*" — HTML5 hint for iOS to render the keypad.
 *   • onChange filter — strips anything that isn't 0-9 so pasted junk
 *     (spaces, commas, currency symbols) is coerced to a bare digit
 *     string. Empty string stays empty (blank = honest Provisional).
 *
 * The visible value always matches what user code holds in state, and
 * mouse-wheel scrolling over the field does not mutate anything.
 */
export const NumericInput = React.forwardRef(function NumericInput(
  { value, onChange, ...rest },
  ref
) {
  const handleChange = React.useCallback(
    (e) => {
      const raw = e.target.value ?? "";
      // Strip anything that isn't a digit. Blank stays blank so the
      // Verified-mode blank-consumer guard can flag it as Provisional.
      const digitsOnly = raw.replace(/[^0-9]/g, "");
      if (digitsOnly === raw) {
        onChange(e);
        return;
      }
      // Synthesise a fake event with the sanitised value so the parent
      // handler doesn't have to know about the wrapper.
      const synthetic = { ...e, target: { ...e.target, value: digitsOnly } };
      onChange(synthetic);
    },
    [onChange]
  );

  return (
    <Input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="off"
      value={value ?? ""}
      onChange={handleChange}
      ref={ref}
      {...rest}
    />
  );
});
